'use client';

import { clsx } from 'clsx';
import {
  useRef,
  useState,
  useEffect,
  ReactElement,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';

import {
  IconX,
  IconCheck,
  IconSearch,
  IconChevronDown,
  IconCircleFilled,
} from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';

import { UserAvatarList } from '../Avatar';
import Input from '../Input/Input';

type TOption =
  | { id: string; name: string; image?: string }
  | { value: string; label: string; image?: string };

interface IDropdownProps {
  options: TOption[];
  icon?: ReactElement;
  name?: string;
  isRequired?: boolean;
  error?: string | string[];
  selected: string | string[];
  disabled?: boolean;
  onChange: (option: string | string[]) => void;
  className?: string;
  rootClassName?: string;
  placeholder?: string;
  isClearable?: boolean;
  dropdownClassName?: string;
  showProfileImage?: boolean;
  disabledOptions?: string[];
  mainClassName?: string;
  isColorBadge?: boolean;
  selectedOptionMaxWidth?: string;
  optionClassName?: string;
  isSearch?: boolean;
  isTextFormat?: boolean;
  isMulti?: boolean;
  isLoading?: boolean;
  dataTestId?: string;
}

const Select = ({
  options,
  name,
  error,
  isRequired,
  icon,
  selected,
  onChange,
  disabled,
  rootClassName,
  className,
  placeholder,
  dropdownClassName,
  isClearable = false,
  disabledOptions = [],
  showProfileImage = false,
  mainClassName,
  isColorBadge = false,
  selectedOptionMaxWidth = 'max-w-[12rem]',
  optionClassName,
  isSearch = false,
  isTextFormat = false,
  isMulti = false,
  isLoading = false,
  dataTestId = 'select',
}: IDropdownProps) => {
  const { formatText } = useFormatText();
  const direction = useSettingsStore((state) => state.settings.direction);
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  // Ref for trigger/button container
  const buttonRef = useRef<HTMLDivElement>(null);
  // Ref for the portal dropdown content
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { register, watch, setValue } = useForm();

  const searchTerm = watch('search');

  useLayoutEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearch(searchTerm?.trim());
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const getOptionValue = (option: TOption) => {
    if ('id' in option) return option.id;
    return option.value;
  };
  const getOptionLabel = (option: TOption) => {
    const label = 'name' in option ? option.name : option.label;
    if (isTextFormat) {
      return formatText(label);
    }
    return label;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideTrigger = !!buttonRef.current?.contains(target);
      const clickedInsideDropdown = !!dropdownRef.current?.contains(target);
      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      // Check if the scroll event target is outside the dropdown and button
      if (
        dropdownRef.current &&
        buttonRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    } else {
      setValue('search', '');
      setSearch(null);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Handle dropdown positioning
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = document.documentElement.clientHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;

      setOpenUpwards(shouldOpenUpwards);
      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleMultiSelect = (optionValue: string) => {
    const selectedArray = Array.isArray(selected) ? selected : [];
    const isSelected = selectedArray.includes(optionValue);
    const newSelected = isSelected
      ? selectedArray.filter((v) => v !== optionValue)
      : [...selectedArray, optionValue];
    onChange(newSelected);
  };

  const handleSingleSelect = (optionValue: string) => {
    // Only call onChange when actually selecting a different value
    if (optionValue !== selected) {
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  const clearMulti = () => {
    onChange([]);
  };

  const clearSingle = () => {
    if (selected) {
      onChange('');
    }
  };

  // Handle clear action
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isMulti) {
      clearMulti();
    } else {
      clearSingle();
    }
  };

  // Handle dropdown toggle - this should NOT trigger onChange
  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const filteredOptions = options?.filter((option) =>
    getOptionLabel(option)
      ?.toLowerCase()
      ?.includes((search ?? '')?.toLowerCase())
  );

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isMulti) return;

    const enabledOptions =
      filteredOptions?.filter(
        (option) => !disabledOptions.includes(String(getOptionValue(option)))
      ) || [];
    const enabledValues = enabledOptions.map((option) =>
      getOptionValue(option)
    );

    const selectedArray = Array.isArray(selected) ? selected : [];
    const allSelected =
      enabledValues.length > 0 &&
      enabledValues.every((val) => selectedArray.includes(val));

    let newSelected: string[] = [];
    if (allSelected) {
      newSelected = selectedArray.filter((val) => !enabledValues.includes(val));
    } else {
      newSelected = [...new Set([...selectedArray, ...enabledValues])];
    }
    onChange(newSelected);
  };

  const isAllSelected = (() => {
    const enabledOptions =
      filteredOptions?.filter(
        (option) => !disabledOptions.includes(String(getOptionValue(option)))
      ) || [];
    const enabledValues = enabledOptions.map((option) =>
      getOptionValue(option)
    );
    const selectedArray = Array.isArray(selected) ? selected : [];
    return (
      enabledValues.length > 0 &&
      enabledValues.every((val) => selectedArray.includes(val))
    );
  })();

  return (
    <div className={clsx('flex flex-col gap-1.5', mainClassName)}>
      {name && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-neutral-primary leading-5 capitalize "
        >
          {name
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())}{' '}
          {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className={clsx('relative ', rootClassName)} ref={buttonRef}>
        {/* Custom Icon on Left */}
        {icon && (
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            {icon}
          </div>
        )}

        {/* Dropdown Button */}
        <button
          type="button"
          className={clsx(
            'w-full py-2.5 bg-white border text-sm border-neutral-200 rounded-lg flex justify-between gap-2 items-center cursor-pointer px-4 focus-within:outline-2 focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff] ',
            disabled &&
              'border-neutral-200! bg-neutral-50! cursor-not-allowed! text-neutral-disabled focus:outline-none',
            icon && 'pl-11',
            error && 'border-red-500',
            className
          )}
          onClick={handleDropdownToggle}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {(() => {
              if (isMulti && Array.isArray(selected)) {
                const selectedOptions = options.filter((option) =>
                  selected.includes(getOptionValue(option))
                );
                if (selectedOptions.length === 0) {
                  return (
                    <span className="text-neutral-secondary font-medium">
                      {placeholder}
                    </span>
                  );
                }
                const displayText = selectedOptions
                  .map((option) => getOptionLabel(option))
                  .join(', ');
                return (
                  <span className="truncate text-sm text-neutral-primary font-medium">
                    {displayText}
                  </span>
                );
              }

              const selectedOption = options.find(
                (option) =>
                  getOptionValue(option) === selected ||
                  getOptionLabel(option) === selected
              );

              return (
                <>
                  {!isColorBadge ? (
                    showProfileImage &&
                    selectedOption && (
                      <div className="shrink-0">
                        {selectedOption?.image ? (
                          <img
                            src={selectedOption.image}
                            alt={getOptionLabel(selectedOption)}
                            className="w-6 h-6 rounded-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const sibling =
                                target.nextElementSibling as HTMLElement;
                              if (sibling) {
                                sibling.classList.remove('hidden');
                              }
                            }}
                          />
                        ) : (
                          <UserAvatarList
                            user={getOptionLabel(selectedOption)}
                            className="w-6! h-6! text-[0.625rem]! min-w-6!"
                          />
                        )}
                      </div>
                    )
                  ) : (
                    <IconCircleFilled
                      size={16}
                      color={
                        selectedOption
                          ? getOptionValue(selectedOption)
                          : '#000000'
                      }
                    />
                  )}
                  <span className={clsx('truncate', selectedOptionMaxWidth)}>
                    {options.find(
                      (option) =>
                        getOptionValue(option) === selected ||
                        getOptionLabel(option) === selected
                    ) ? (
                      getOptionLabel(
                        options.find(
                          (option) =>
                            getOptionValue(option) === selected ||
                            getOptionLabel(option) === selected
                        )!
                      )
                    ) : (
                      <span className="text-neutral-secondary font-medium">
                        {placeholder}
                      </span>
                    )}
                  </span>
                </>
              );
            })()}
          </div>

          <div className="flex items-center justify-center gap-2">
            {isClearable &&
              ((isMulti && Array.isArray(selected) && selected.length > 0) ||
                (!isMulti && selected)) && (
                <span
                  className="rounded-full hover:bg-neutral-100 h-5 flex items-center justify-center w-5"
                  onClick={handleClear}
                >
                  <IconX size={18} color="#3d3d3d" />
                </span>
              )}
            <IconChevronDown
              size={18}
              className={clsx(
                'transition-transform duration-300 text-neutral-disabled',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </button>

        {/* Dropdown Options Portal */}
        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className={clsx(
                'fixed z-9999 flex-col flex bg-white border border-neutral-200 gap-0.5 rounded-lg shadow-lg overflow-x-hidden overflow-y-auto max-h-60',
                dropdownClassName
              )}
              style={{
                top: openUpwards ? 'auto' : dropdownPosition.top,
                bottom: openUpwards
                  ? window.innerHeight - dropdownPosition.top
                  : 'auto',
                left: direction === 'rtl' ? undefined : dropdownPosition.left,
                right:
                  direction === 'rtl'
                    ? window.innerWidth -
                      dropdownPosition.left -
                      dropdownPosition.width
                    : undefined,
                width: dropdownPosition.width,
              }}
            >
              <div className="flex w-full flex-col sticky top-0 bg-white z-10  p-1">
                {isSearch && (
                  <div className="p-2 pb-0">
                    <Input
                      name="search"
                      placeholder="Search..."
                      register={register}
                      autoFocus
                      leftIcon={{
                        icon: (
                          <IconSearch
                            width={20}
                            height={20}
                            stroke={1.5}
                            className="text-neutral-400"
                          />
                        ),
                      }}
                      inputClassName="!py-2.5 !px-3 !text-sm ! !text-neutral-600"
                    />
                  </div>
                )}

                {isMulti && filteredOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={
                      !filteredOptions ||
                      filteredOptions.length === 0 ||
                      filteredOptions.every((opt) =>
                        disabledOptions.includes(String(getOptionValue(opt)))
                      )
                    }
                    className="pt-3 flex justify-start gap-1.5 items-center px-2 text-neutral-900 cursor-pointer rounded-lg hover:bg-neutral-50 text-left w-full"
                  >
                    {isAllSelected
                      ? formatText('common.actions.deselect-all')
                      : formatText('common.actions.select-all')}
                  </button>
                )}
              </div>
              {isLoading && (
                <div className="p-4 text-center flex justify-center items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-default"></div>
                </div>
              )}
              {filteredOptions?.length === 0 && !isLoading && (
                <div className="p-4 text-center text-neutral-500 text-sm">
                  {formatText('common.no-data-found')}
                </div>
              )}
              <div className="p-1 space-y-1">
                {filteredOptions?.map((option) => (
                  <button
                    key={getOptionValue(option)}
                    type="button" // Explicitly set type to button
                    className={clsx(
                      'py-1 flex justify-start gap-1.5 items-center px-2 text-neutral-900 cursor-pointer rounded-lg hover:bg-neutral-50 text-left w-full',
                      (() => {
                        const isSelected = isMulti
                          ? Array.isArray(selected) &&
                            selected.includes(getOptionValue(option))
                          : getOptionLabel(option) === selected ||
                            getOptionValue(option) === selected;

                        return isSelected
                          ? 'bg-brand-light-50'
                          : 'bg-shades-white';
                      })(),
                      {
                        'text-gray-400! cursor-not-allowed!':
                          disabledOptions.includes(
                            String(getOptionValue(option))
                          ),
                      },
                      optionClassName
                    )}
                    onClick={() => {
                      if (
                        disabledOptions.includes(String(getOptionValue(option)))
                      )
                        return;

                      const val = getOptionValue(option);
                      if (isMulti) {
                        handleMultiSelect(val);
                      } else {
                        handleSingleSelect(val);
                      }
                    }}
                    disabled={disabledOptions.includes(
                      String(getOptionValue(option))
                    )}
                  >
                    {!isColorBadge ? (
                      showProfileImage && (
                        <div className="mr-2">
                          {option?.image ? (
                            <img
                              src={option?.image}
                              alt={getOptionLabel(option)}
                              className="w-6 h-6 rounded-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const sibling =
                                  target.nextElementSibling as HTMLElement;
                                if (sibling) {
                                  sibling.classList.remove('hidden');
                                }
                              }}
                            />
                          ) : (
                            <UserAvatarList
                              user={getOptionLabel(option)}
                              className="w-6! h-6! text-[0.625rem]! min-w-6!"
                            />
                          )}
                        </div>
                      )
                    ) : (
                      <IconCircleFilled
                        size={16}
                        color={option ? getOptionValue(option) : '#000000'}
                      />
                    )}
                    <span
                      className="truncate max-w-full"
                      style={{ display: 'inline-block', minWidth: 0 }}
                      title={getOptionLabel(option)}
                    >
                      {getOptionLabel(option)}
                    </span>
                    {(isMulti && Array.isArray(selected)
                      ? selected.includes(getOptionValue(option))
                      : getOptionLabel(option) === selected ||
                        getOptionValue(option) === selected) && (
                      <IconCheck
                        size={18}
                        className="text-brand-default ms-2 absolute right-4 rtl:left-4 rtl:right-auto"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )}
      </div>
      {error &&
        (typeof error === 'object' ? (
          error.map((e, i) => (
            <p key={i} className="text-sm font-normal text-red-500">
              {e}
            </p>
          ))
        ) : (
          <p className="text-sm font-normal text-red-500">{error}</p>
        ))}
    </div>
  );
};

export default Select;
